/**
 * Boot configuration, read from the script tag and the URL.
 *
 * Whether the widget runs at all is the *project's* decision now, fetched in
 * `mode.ts`. This file answers the narrower question the project cannot: which
 * project, and did this particular visitor ask.
 *
 * Two rules govern everything here, both aimed at the same failure: feedback
 * from a client landing in an internal ClickUp list, or vice versa.
 *
 *  1. In `link` mode nothing runs until an explicit `?annotate=1`, so a clean URL
 *     on a machine that has never been given a review link is a clean page and
 *     the tag can stay in the HTML permanently. In `open` mode the project has
 *     already answered for every visitor, and this rule does not apply.
 *  2. A `?project=` in the URL is honoured only if the script tag listed it in
 *     `data-projects-allowed`. Anything else refuses to start rather than
 *     quietly falling back to the default project.
 *
 * In `link` mode review then persists for the rest of the browsing session,
 * because a site is more than one page and asking a reviewer to hand-edit the URL
 * of every page they visit is how you get no feedback on page two.
 * `sessionStorage` and not `localStorage`: a review lasts a sitting, and a flag
 * that outlives the tab is a flag that reappears months later while the site is
 * being demoed to a client.
 *
 * The carried session stores the *project*, not just an on switch, and rule 2 is
 * re-applied to it on every page. Carrying only the switch would mean a client on
 * `?annotate=1&project=acme-client` posting into the internal list from page two
 * onwards — the precise failure both rules exist to prevent, reintroduced by the
 * convenience.
 */

export interface WidgetConfig {
  apiBase: string;
  projectId: string;
  projectsAllowed: string[];
  focusThreadId: string | null;
}

export type Boot =
  /** The tag and the URL are usable. Whether the widget actually starts is the
   *  project's decision, asked for separately — see `mode.ts`. */
  | { ok: true; config: WidgetConfig; requestedByUrl: boolean }
  | { ok: false; reason: string; loud: boolean };

export function readConfig(script: HTMLScriptElement | null): Boot {
  if (!script) {
    return { ok: false, reason: 'could not find its own <script> tag', loud: true };
  }

  const params = new URLSearchParams(window.location.search);

  // `?annotate=0` is the way out, and is checked before anything else: it beats
  // an `open` project too, and it means an explicit opt-out costs no request.
  if (params.get('annotate') === '0') {
    endReviewSession();
    return { ok: false, reason: 'review mode turned off', loud: false };
  }

  const fromUrl = params.get('annotate') === '1' || window.location.hash === '#annotate';
  const carried = fromUrl ? null : carriedSession();

  // No early return for "nobody asked": in `open` mode the project answers for
  // the visitor, so the tag still has to be read and the mode still has to be
  // fetched. What used to be free is now one cached request per page load.
  if (window.location.protocol !== 'http:' && window.location.protocol !== 'https:') {
    return {
      ok: false,
      reason: `annotate only supports server-hosted pages, not ${window.location.protocol}`,
      loud: true,
    };
  }

  const defaultProject = script.getAttribute('data-project');
  if (!defaultProject) {
    return { ok: false, reason: 'the script tag has no data-project', loud: true };
  }

  const allowedAttr = script.getAttribute('data-projects-allowed');
  const projectsAllowed = allowedAttr
    ? allowedAttr.split(',').map((s) => s.trim()).filter(Boolean)
    : [defaultProject];
  if (!projectsAllowed.includes(defaultProject)) projectsAllowed.push(defaultProject);

  // The URL wins over the carried session, so a reviewer can switch projects
  // mid-session by following a different link.
  const requested = params.get('project') ?? carried;
  if (requested && !projectsAllowed.includes(requested)) {
    const source = params.get('project')
      ? 'the URL asks for'
      : 'this review session is carrying';
    return {
      ok: false,
      reason:
        `${source} project "${requested}", which this page does not permit. ` +
        `Add it to data-projects-allowed if that is intended, or leave review ` +
        `mode with ?annotate=0.`,
      loud: true,
    };
  }

  let apiBase: string;
  try {
    apiBase = new URL(script.src, window.location.href).origin;
  } catch {
    return { ok: false, reason: 'could not work out the API origin from the script src', loud: true };
  }

  return {
    ok: true,
    requestedByUrl: Boolean(fromUrl || carried),
    config: {
      apiBase,
      projectId: requested ?? defaultProject,
      projectsAllowed,
      focusThreadId: params.get('thread'),
    },
  };
}

/**
 * Called once the project has agreed the widget should run, and only in `link`
 * mode. Refreshed on every page so the session follows the reviewer for as long
 * as they keep reviewing.
 *
 * Not written in `open` mode: there the project is what grants access, and a
 * session written there would keep one visitor reviewing after an admin had
 * turned the project back down to `link`.
 */
export function beginReviewSession(projectId: string): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, projectId);
  } catch {
    /* private browsing — the reviewer keeps the parameter instead */
  }
}

const SESSION_KEY = 'annotate.review-session';

/** The project this session is reviewing, or null if it is not in review mode. */
function carriedSession(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_KEY);
  } catch {
    // Private browsing, or storage disabled. Review mode then behaves exactly as
    // it did before it was sticky: per-URL, and no worse.
    return null;
  }
}

export function endReviewSession(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* nothing was stored in the first place */
  }
}

const NAME_KEY = 'annotate.reviewer-name';

export function storedName(): string {
  try {
    return window.localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function storeName(name: string): void {
  try {
    window.localStorage.setItem(NAME_KEY, name);
  } catch {
    /* private browsing — the name just will not persist */
  }
}
